import React from "react";
import dayjs from 'dayjs';
import {
    LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker"; // Changed from DatePicker
import {
    useSplitFieldProps,
    useParsedFormat,
    usePickersContext
} from '@mui/x-date-pickers/hooks';
import {IconButton, Stack} from '@mui/material';
import { Button } from "@base-ui/react/button";

import { ReactComponent as CALENDAR } from "../images/calendarIcon.svg";
import { ReactComponent as LEFT } from "../images/arrow-left-gray.svg";
import { ReactComponent as RIGHT } from "../images/arrow-right-gray.svg";
import classes from "./YuJaDatePicker.module.css";
import { useTranslation } from "react-i18next";

const ReadOnlyDateTimeField = React.forwardRef((props, ref) => {
    // Note: 'date' changed to 'dateTime' for internal split logic
    const { internalProps, forwardedProps } = useSplitFieldProps(props, 'dateTime');
    const { value, timezone, format } = internalProps;
    const { InputProps, sx } = forwardedProps;
    const handleRef = ref || InputProps?.ref;


    const pickersContext = usePickersContext();
    const parsedFormat = useParsedFormat(internalProps);

    const handleTogglePicker = (event) => {
        if (pickersContext.open) {
            pickersContext.onClose(event);
        } else {
            pickersContext.onOpen(event);
        }
    };

    return (
        <Button
            aria-haspopup="dialog"
            aria-expanded={pickersContext.open ? "true" : "false"}
            onClick={handleTogglePicker}
            className={classes.dateInput}
            ref={handleRef}
            style={{ width: 210, ...sx }}
        >
            {!value && <span className={classes.placeholder}>{parsedFormat}</span>}
            {/* format(format) will now include time based on the format prop passed below */}
            {!!value && <span>{value.format(format)}</span>}
            <CALENDAR color="action" style={{ flexShrink: 0 }} />
        </Button>
    );
})

// Custom Header remains largely the same
function MyCustomHeader(props) {
    const { t } = useTranslation();
    const { currentMonth, onMonthChange, leftArrowButtonText, onViewChange, view, rightArrowButtonText } = props;

    const selectNextMonth = () => onMonthChange(currentMonth.add(1, 'month'), 'left');
    const selectPreviousMonth = () => onMonthChange(currentMonth.add(-1, 'month'), 'right');

    return (
        <Stack direction="row" alignItems={"center"} justifyContent={"space-between"} sx={{ padding: '8px 16px', minHeight: 40 }}>
            <IconButton onClick={selectPreviousMonth} title={leftArrowButtonText} size="small" aria-label={t("accessibility.previousMonthText")}>
                <LEFT />
            </IconButton>
            <Button
                onClick={() => onViewChange(view === 'year' ? 'day' : 'year')}
                className={classes.toggleBtn}
                aria-label={view === 'year' ? t("accessibility.switchToCalendarView") : t("accessibility.switchToYearView")}
            >
                {currentMonth.format('MMMM YYYY')}
            </Button>
            <IconButton onClick={selectNextMonth} title={rightArrowButtonText} size="small" aria-label={t("accessibility.nextMonthText")}>
                <RIGHT />
            </IconButton>
        </Stack>
    );
}





export default function YuJaDateTimePicker({
                                               date,
                                               onChange,
                                               minDate,
                                               maxDate,
                                               textFieldStyle = {},
                                               disabled = false,
                                               ariaLabel,
}) {
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
                disabled={disabled}
                autoFocus={true}
                // closeOnSelect={false} // Recommendation: false for DateTime so users can pick time after date
                minDateTime={!!minDate ? dayjs(minDate) : null}
                maxDateTime={!!maxDate ? dayjs(maxDate) : null}
                minutesStep={1}
                slots={{
                    field: ReadOnlyDateTimeField,
                    calendarHeader: MyCustomHeader,
                    // layout: CustomLayout, // Overrides the entire structure
                }}
                slotProps={{
                    field: {
                        sx: {
                            minWidth: 'unset !important',
                            ...textFieldStyle
                        }
                    },
                    popper: {
                        'aria-modal': 'true',
                        'aria-label': !!ariaLabel ? ariaLabel : 'Date and time picker',
                    },
                    desktopPaper: {
                        sx: {
                            margin: '5px 0',
                            '& .MuiDateCalendar-root': {
                                height: 'auto',
                            },

                            '& .MuiDayCalendar-slideTransition': {
                                height: 'auto',
                                minHeight: '230px'
                            },
                            '& .MuiDayCalendar-monthContainer': {
                                padding: '5px 0',
                            },
                            '& .MuiButtonBase-root:focus-visible, & .MuiPickersYear-yearButton:focus-visible': {
                                outlineOffset: '2px !important',
                            },
                            '& .Mui-focusVisible:not(.Mui-selected), & .MuiPickersYear-yearButton:focus-visible:not(.Mui-selected)': {
                                backgroundColor: '#F7F6FB !important',
                                background: '#F7F6FB !important'
                            },

                            '& .MuiPickersDay-root': {
                                margin: '2px'
                            },
                            '& .MuiPickersDay-today': {
                                backgroundColor: 'transparent'
                            },
                            '& .Mui-selected': {
                                backgroundColor: '#4a148c !important',
                                color: '#fff',
                            },
                            '& .MuiPickersDay-root:not(.Mui-selected):hover, & .MuiPickersYear-yearButton:not(.Mui-selected):hover': {
                                backgroundColor: '#F7F6FB !important',
                            },

                            '& .MuiPickersYear-yearButton': {
                                width: '42px',
                                height: '42px',
                                borderRadius: '50%',
                                fontSize: '12px'
                            },
                            '& .MuiMultiSectionDigitalClockSection-item': {
                                fontSize: 12,
                                borderRadius: 1,
                            },
                            '& .MuiMultiSectionDigitalClockSection-item:focus-visible': {
                                outlineOffset: '1px !important',
                            },
                            '& .MuiMultiSectionDigitalClockSection-item:not(.Mui-selected):hover': {
                                backgroundColor: '#F7F6FB !important',
                            }

                        },
                    },

                    mobilePaper: {
                        sx: {
                            '& .MuiDateCalendar-root': {
                                height: 'auto',
                            },

                            '& .MuiDayCalendar-slideTransition': {
                                height: 'auto',
                                minHeight: '230px'
                            },
                            '& .MuiDayCalendar-monthContainer': {
                                padding: '5px 0',
                            },
                            '& .MuiButtonBase-root:focus-visible, & .MuiPickersYear-yearButton:focus-visible': {
                                outlineOffset: '2px !important',
                            },
                            '& .Mui-focusVisible:not(.Mui-selected), & .MuiPickersYear-yearButton:focus-visible:not(.Mui-selected)': {
                                backgroundColor: '#F7F6FB !important',
                                background: '#F7F6FB !important'
                            },

                            '& .MuiPickersDay-root': {
                                margin: '2px'
                            },
                            '& .MuiPickersDay-today': {
                                backgroundColor: 'transparent'
                            },
                            '& .MuiPickersDay-root.Mui-selected': {
                                backgroundColor: '#4a148c !important',
                                color: '#fff',
                            },

                            '& .MuiPickersYear-yearButton.Mui-selected': {
                                backgroundColor: '#4a148c !important',
                                color: '#fff',
                            },

                            '& .Mui-selected .MuiSvgIcon-root': {
                                fill: '#4a148c',
                            },
                            '& .MuiTabs-indicator, & .MuiClock-pin, & .MuiClockPointer-root, & .MuiClockPointer-thumb': {
                                backgroundColor: '#4a148c',
                            },
                            
                            '& .MuiClockPointer-thumb': {
                                borderColor: '#4a148c'
                            },
                            '& .MuiPickersDay-root:not(.Mui-selected):hover, & .MuiPickersYear-yearButton:not(.Mui-selected):hover': {
                                backgroundColor: '#F7F6FB !important',
                            },

                            '& .MuiPickersYear-yearButton': {
                                width: '42px',
                                height: '42px',
                                borderRadius: '50%',
                                fontSize: '12px'
                            },
                            '& .MuiMultiSectionDigitalClockSection-item': {
                                fontSize: 12,
                                borderRadius: 1,
                            },
                            '& .MuiMultiSectionDigitalClockSection-item:focus-visible': {
                                outlineOffset: '1px !important',
                            },
                            '& .MuiMultiSectionDigitalClockSection-item:not(.Mui-selected):hover': {
                                backgroundColor: '#F7F6FB !important',
                            }

                        },
                    },
                    // Add Action Bar if you want a "Close" or "OK" button to confirm selection
                    actionBar: {
                        actions: [],
                    }
                }}
                // Updated format to include time
                format={'YYYY-MM-DD hh:mm A'}
                value={!!date ? dayjs(date) : null}
                onChange={val => onChange(!!val ? val.toDate() : null)}
            />
        </LocalizationProvider>
    );
}